import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { API_ENDPOINTS } from "../config/api";
import articles from "../articles/index";
import "bootstrap/dist/css/bootstrap.min.css";
import "../styles/home.css";
import {
  ArrowRight,
  Linkedin,
  MessageSquare,
  Database,
  Shield,
  ChartBar,
  Activity,
  Rocket,
  Brain,
  Target,
  Github,
} from "lucide-react";
import SeoHead from "../components/SeoHead";
import TypedLite from "../components/TypedLite";

const Home = () => {
  const [featured, setFeatured] = useState([]);
  const [projectStats, setProjectStats] = useState({ total: 0, ai: 0, ops: 0 });

  const latestArticles = useMemo(
    () =>
      [...articles]
        .sort((a, b) => new Date(b.date) - new Date(a.date))
        .slice(0, 3),
    []
  );

  const computeProjectStats = (list) => {
    const aiKeywords = ["ai", "ml", "machine learning", "forecast", "nlp", "automation", "predict", "chatbot"];
    const opsKeywords = ["ops", "operations", "platform", "dispatch", "lifecycle", "management", "reconciliation"];
    const norm = (txt = "") => txt.toLowerCase();
    const ai = list.filter(
      (p) =>
        aiKeywords.some((k) => norm(p.title || "").includes(k)) ||
        aiKeywords.some((k) => norm(p.sections?.Introduction || "").includes(k))
    ).length;
    const ops = list.filter(
      (p) =>
        opsKeywords.some((k) => norm(p.title || "").includes(k)) ||
        opsKeywords.some((k) => norm(p.sections?.Introduction || "").includes(k))
    ).length;
    return { total: list.length, ai, ops };
  };

  useEffect(() => {
    const pickRandom = (arr, n) => {
      if (!arr || arr.length === 0) return [];
      const copy = [...arr];
      for (let i = copy.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [copy[i], copy[j]] = [copy[j], copy[i]];
      }
      return copy.slice(0, n);
    };

    const fetchProjects = async () => {
      try {
        const resp = await axios.get(API_ENDPOINTS.getArticles);
        if (resp.status === 200 && resp.data && resp.data.articles) {
          setFeatured(pickRandom(resp.data.articles, 3));
          setProjectStats(computeProjectStats(resp.data.articles));
        }
      } catch (err) {
        // fallback to static cards when API is not available
      }
    };

    fetchProjects();
  }, []);

  const brandPillars = [
    {
      title: "Mobility operations",
      desc: "Lead transportation network, paratransit, and mobility operations with audit-ready accountability and contract discipline.",
      icon: Activity,
    },
    {
      title: "Data science and AI",
      desc: "Modeling, NLP, Demand Forecasting, and anomaly detection that make decisions faster, safer, and more transparent.",
      icon: Brain,
    },
    {
      title: "Product and platforms",
      desc: "Full-stack delivery across MERN, BI, and cloud to get tools in the hands of ops teams quickly & effectively.",
      icon: Rocket,
    },
    {
      title: "Governance and risk",
      desc: "Ethical, auditable systems that pass FTA, ADA, and internal reviews without surprises.",
      icon: Shield,
    },
  ];

  const impactStats = [
    { label: "Monthly trips governed", value: "56k+", sub: "through mobility data, audits, and reporting" },
    { label: "Drivers and staff led", value: "2000+", sub: "across taxi and transit teams" },
    { label: "AI/ML initiatives", value: "22+", sub: "automation, forecasting, NLP" },
    { label: "Board-ready reports", value: "Every month", sub: "executive and stakeholder packs" },
  ];

  const focusNow = [
    {
      title: "Transportation network success",
      detail: "Supporting provider performance, contract compliance, safety, and service reliability across paratransit and NEMT networks.",
      icon: Database,
    },
    {
      title: "Launching open, ethical AI",
      detail: "Building ATS and decision tools that avoid bias, keep context, and prove compliance.",
      icon: Target,
    },
    {
      title: "Founder mindset",
      detail: "From zero to 34+ drivers at Trans Voyage Taxi; hands-on with finance, ops, and tech delivery.",
      icon: ChartBar,
    },
  ];

  const heroBadges = ["Founder / Operator","Executive Leader", "Data & Analytics Executive"];

  const typedStrings = useMemo(
    () => [
      "Mobility & Transportation Analytics Leader",
      "Business Intelligence Executive",
      "Data Governance",
      "Compliance and Audit Specialist",
      "Service Performance Optimization",
      "AI & Machine Learning Advocate"
    ],
    []
  );

  return (
    <div className="home-container">
      <SeoHead
        title="Transportation, Mobility, Analytics, and Operations Leader"
        description="Mohamed Gad leads mobility and transportation operations through analytics, business intelligence, data governance, compliance, and accountable technology."
        keywords={[
          "Mohamed Gad",
          "data science",
          "AI",
          "transportation analytics",
          "mobility data",
          "ethical AI",
          "operational analytics",
        ]}
        canonical="https://www.mohamedgad.com/"
        jsonLd={[
          {
            "@context": "https://schema.org",
            "@type": "Person",
            "name": "Mohamed Gad",
            "alternateName": ["Mohamed M. Gad"],
            "url": "https://www.mohamedgad.com/",
            "image": "https://www.mohamedgad.com/mohamed-gad.png",
            "jobTitle": [
              "Network Success Sr Manager - East",
              "Transportation and Mobility Executive",
              "Business Intelligence and Analytics Leader",
            ],
            "worksFor": {
              "@type": "Organization",
              "name": "Big Star Transit, LLC",
              "url": "https://www.bigstartransit.com/",
            },
            "knowsAbout": [
              "AI",
              "machine learning",
              "data science",
              "transportation analytics",
              "mobility systems",
              "transportation network success",
              "contract compliance",
              "ethical AI",
              "operational analytics",
              "governance and compliance",
            ],
            "sameAs": [
              "https://www.linkedin.com/in/mohamed-gad-msds-6b286b1b6/",
              "https://github.com/OldAlexhub",
              "https://www.facebook.com/MohamedMMagdy",
            ],
            "contactPoint": {
              "@type": "ContactPoint",
              "contactType": "Business",
              "email": "mohamed@mohamedgad.com",
              "areaServed": "Global",
              "availableLanguage": ["English", "Arabic"],
            },
          },
          {
            "@context": "https://schema.org",
            "@type": "Organization",
            "name": "Old Alex Hub, LLC",
            "url": "https://www.mohamedgad.com/",
            "logo": "https://www.mohamedgad.com/mohamed-gad.png",
            "founder": "Mohamed Gad",
            "sameAs": [
              "https://www.linkedin.com/in/mohamed-gad-msds-6b286b1b6/",
              "https://github.com/OldAlexhub",
            ],
          },
          {
            "@context": "https://schema.org",
            "@type": "WebSite",
            "name": "Mohamed Gad",
            "url": "https://www.mohamedgad.com/",
            "description": "Transportation and mobility leader focused on analytics, business intelligence, data governance, and accountable operations.",
            "publisher": {
              "@type": "Person",
              "name": "Mohamed Gad",
              "url": "https://www.mohamedgad.com/",
            },
            "potentialAction": {
              "@type": "SearchAction",
              "target": "https://www.google.com/search-q=Mohamed+Gad+{search_term_string}",
              "query-input": "required name=search_term_string",
            },
          },
          {
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            "itemListElement": [
              {
                "@type": "ListItem",
                "position": 1,
                "name": "Home",
                "item": "https://www.mohamedgad.com/",
              },
              {
                "@type": "ListItem",
                "position": 2,
                "name": "Projects",
                "item": "https://www.mohamedgad.com/projects",
              },
              {
                "@type": "ListItem",
                "position": 3,
                "name": "Articles",
                "item": "https://www.mohamedgad.com/articlelist",
              },
            ],
          },
        ]}
      />
      {/* Hero */}
      <section className="hero">
        <div className="container">
          <div className="hero-grid">
            <div className="hero-copy">
              <div className="badge-row">
                {heroBadges.map((b) => (
                  <span key={b} className="pill">
                    {b}
                  </span>
                ))}
              </div>
              <h1 className="display-5 fw-bold text-glow">Mohamed Gad</h1>
              <p className="lead hero-lead">
              I bridge strategy, operations, and AI to design and run real-world systems.
              </p>
              <h2
                className="typed-line"
                aria-live="polite"
                style={{
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  minHeight: "1.2em",
                }}
              >
                <TypedLite
                  strings={typedStrings}
                  typeSpeed={50}
                  backSpeed={40}
                  backDelay={1500}
                  loop
                />
              </h2>
              <div className="hero-actions">
                <a href="/Mohamed-Gad-CV.pdf" className="btn btn-primary" download>
                  Download CV
                </a>
                <Link to="/projects" className="btn btn-outline-primary">
                  View Projects
                </Link>
                <Link to="/contactus" className="btn btn-link">
                  Book a call <ArrowRight size={16} className="ms-1" />
                </Link>
              </div>
              <div className="hero-meta">
                <div>
                  <div className="meta-label">Location</div>
                  <div className="meta-value">Orlando, FL</div>
                </div>
                <div>
                  <div className="meta-label">Email</div>
                  <div className="meta-value">mohamed@mohamedgad.com</div>
                </div>
                <div>
                    <div className="meta-label">Focus</div>
                    <div className="meta-value">
                      Operational analytics, AI for efficiency, data governance
                    </div>
                </div>
              </div>
            </div>
            <div className="hero-card shadow-lg">
              <div className="profile-wrap text-center">
                <img
                  src="/mohamed-gad.png"
                  alt="Mohamed Gad"
                  className="profile-avatar"
                  loading="lazy"
                />
                <p className="small profile-role mb-1">Network Success Sr Manager - East @ Big Star Transit</p>
                <p className="small profile-meta">Founder, <a href="https://www.transvoyagetaxi.com/" target="_blank" rel="noreferrer" className="trans-link">Trans Voyage Taxi</a> <span className="meta-sep">|</span> <Link to="/oldalexhub" className="old-link">Old Alex Hub</Link></p>
              </div>
              <div className="hero-highlight">
                <div>
                  <div className="value">56k+</div>
                  <div className="label">Monthly trips governed</div>
                </div>
                <div>
                  <div className="value">{projectStats.ai || "-"}</div>
                  <div className="label">AI/ML automations shipped</div>
                </div>
                <div>
                  <div className="value">{projectStats.ops || "-"}</div>
                  <div className="label">Ops platforms and systems</div>
                </div>
              </div>
              <div className="hero-links">
                <a
                  href="https://www.linkedin.com/in/mohamed-gad-msds-6b286b1b6/"
                  target="_blank"
                  rel="noreferrer"
                  className="btn btn-dark w-100"
                >
                  <Linkedin size={16} className="me-1" /> Connect on LinkedIn
                </a>
                <a
                  href="https://github.com/OldAlexhub"
                  target="_blank"
                  rel="noreferrer"
                  className="btn btn-outline-light w-100 mt-2"
                >
                  <Github size={16} className="me-1" /> View GitHub
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Impact strip */}
      <section className="impact-strip">
        <div className="container">
          <div className="impact-grid">
            {impactStats.map((s) => (
              <div key={s.label} className="impact-card">
                <div className="impact-value">{s.value}</div>
                <div className="impact-label">{s.label}</div>
                <div className="impact-sub">{s.sub}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Brand pillars */}
      <section className="pillars container my-5">
        <div className="section-heading">
          <div>
            <p className="eyebrow">Brand pillars</p>
            <h2 className="fw-bold">Where I create leverage</h2>
            <p className="text-muted">
              Transportation and mobility executive with more than a decade of leadership across public transit,
              paratransit, NEMT, taxi operations, contractor oversight, analytics, and business ownership. I connect
              field operations, compliance, data governance, and technology into practical systems that improve
              accountability, service reliability, and executive decision-making. AI and data science support the work,
              but the core focus is disciplined operations under real-world regulatory pressure.
            </p>
          </div>
        </div>
        <div className="row g-4">
          {brandPillars.map((p) => {
            const Icon = p.icon;
            return (
              <div key={p.title} className="col-md-6">
                <div className="pillar-card h-100">
                  <div className="icon-pill">
                    <Icon size={18} />
                  </div>
                  <div>
                    <h5 className="mb-1">{p.title}</h5>
                    <p className="text-muted mb-0">{p.desc}</p>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {/* Focus now */}
      <section className="focus-now container my-5">
        <div className="section-heading d-flex align-items-center justify-content-between flex-wrap gap-2">
          <div>
            <p className="eyebrow">Current focus</p>
            <h2 className="fw-bold mb-0">What I am building right now</h2>
          </div>
          <Link to="/projects" className="btn btn-outline-primary btn-sm">
            See projects
          </Link>
        </div>
        <div className="row g-4">
          {focusNow.map((f) => {
            const Icon = f.icon;
            return (
              <div key={f.title} className="col-md-4">
                <div className="focus-card h-100">
                  <div className="icon-pill">
                    <Icon size={18} />
                  </div>
                  <h5 className="mb-1">{f.title}</h5>
                  <p className="text-muted mb-0">{f.detail}</p>
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {/* Featured Projects */}
      <section className="services container my-5">
        <div className="section-heading">
          <div>
            <p className="eyebrow">Selected work</p>
            <h2 className="fw-bold">Featured projects</h2>
            <p className="text-muted">
              AI-assisted ops, reconciliation platforms, ethical ATS, and forecasting tools that turned strategy into measurable results.
            </p>
          </div>
        </div>

        <div className="row g-4">
          {featured && featured.length > 0 ? (
            featured.map((project) => (
              <div key={project._id || project.title} className="col-md-4">
                <div className="p-3 border rounded h-100 shadow-sm d-flex flex-column project-card">
                  {project.image && (
                    <img
                      src={project.image}
                      alt={project.title}
                      className="img-fluid mb-3 rounded"
                      style={{ height: 160, objectFit: "cover" }}
                      loading="lazy"
                    />
                  )}
                  <h5>{project.title}</h5>
                  <p className="text-muted small flex-grow-1">
                    {project.sections && project.sections.Introduction
                      ? project.sections.Introduction.split(" ").slice(0, 35).join(" ") + "..."
                      : project.description || project.summary || ""}
                  </p>
                  <div className="mt-3 d-flex align-items-center justify-content-between">
                    <span className="small text-muted">Case study</span>
                    <Link to={`/projects/${project._id || ""}`} className="btn btn-outline-primary btn-sm">
                      Read more
                    </Link>
                  </div>
                </div>
              </div>
            ))
          ) : (
            <>
              <div className="col-md-4">
                <div className="p-3 border rounded h-100 shadow-sm d-flex flex-column project-card">
                  <div className="mb-3 text-cyan">
                    <Database size={44} />
                  </div>
                  <h5>Full-stack taxi operations platform</h5>
                  <p className="text-muted small flex-grow-1">
                    MERN system for trip reconciliation, live driver monitoring, and automated dispatch; reduced missed trips and improved SLA adherence.
                  </p>
                  <div className="mt-3 d-flex align-items-center justify-content-between">
                    <span className="small text-muted">Case study</span>
                    <Link to="/projects#taxi-platform" className="btn btn-outline-primary btn-sm">
                      Read more
                    </Link>
                  </div>
                </div>
              </div>

              <div className="col-md-4">
                <div className="p-3 border rounded h-100 shadow-sm d-flex flex-column project-card">
                  <div className="mb-3 text-cyan">
                    <Shield size={44} />
                  </div>
                  <h5>AI resume screening engine</h5>
                  <p className="text-muted small flex-grow-1">
                    Ethical ATS using transformers and semantic matching to reduce bias and surface best-fit candidates without keyword dependence.
                  </p>
                  <div className="mt-3 d-flex align-items-center justify-content-between">
                    <span className="small text-muted">Case study</span>
                    <Link to="/projects#ai-ats" className="btn btn-outline-primary btn-sm">
                      Read more
                    </Link>
                  </div>
                </div>
              </div>

              <div className="col-md-4">
                <div className="p-3 border rounded h-100 shadow-sm d-flex flex-column project-card">
                  <div className="mb-3 text-cyan">
                    <ChartBar size={44} />
                  </div>
                  <h5>YouTube trending prediction</h5>
                  <p className="text-muted small flex-grow-1">
                    ML model using metadata and NLP to forecast video virality for content planning and publisher strategy.
                  </p>
                  <div className="mt-3 d-flex align-items-center justify-content-between">
                    <span className="small text-muted">Case study</span>
                    <Link to="/projects#yt-trend" className="btn btn-outline-primary btn-sm">
                      Read more
                    </Link>
                  </div>
                </div>
              </div>
            </>
          )}
        </div>

        <div className="text-center mt-4">
          <Link to="/projects" className="btn btn-outline-primary btn-lg">
            View all projects <ArrowRight size={18} className="ms-1" />
          </Link>
        </div>
      </section>

      {/* Articles */}
      <section className="articles container my-5">
        <div className="section-heading d-flex align-items-center justify-content-between flex-wrap gap-2">
          <div>
            <p className="eyebrow">Latest writing</p>
            <h2 className="fw-bold mb-0">Articles and analysis</h2>
            <p className="text-muted mb-0">
              Data infrastructure, AI governance, and mobility systems - curated from the blog.
            </p>
          </div>
          <Link to="/articlelist" className="btn btn-outline-primary btn-sm">
            Browse all
          </Link>
        </div>
        <div className="row g-4">
          {latestArticles.map((a) => (
            <div key={a.slug} className="col-md-4">
              <Link to={`/articles/${a.slug}`} className="text-decoration-none">
                <div className="card h-100 shadow-sm article-card-home">
                  {a.cover_image && (
                    <img
                      className="article-thumb"
                      src={a.cover_image}
                      srcSet={a.cover_image_srcset}
                      sizes="(max-width: 767px) 100vw, 33vw"
                      alt=""
                      loading="lazy"
                      decoding="async"
                      width="640"
                      height="360"
                    />
                  )}
                  <div className="card-body">
                    <div className="small text-muted mb-1">
                      {a.category} · {new Date(`${a.date}T00:00:00`).toLocaleDateString()}
                    </div>
                    <h5 className="card-title">{a.title}</h5>
                    <p className="text-muted small mb-2">
                      {a.summary}
                    </p>
                    <span className="small text-primary">
                      Read article <ArrowRight size={14} className="ms-1" />
                    </span>
                  </div>
                </div>
              </Link>
            </div>
          ))}
        </div>
      </section>

      {/* Philosophy */}
      <section className="thought-leadership text-center my-5">
        <h3>Operating principle</h3>
        <blockquote className="philosophy-text">
          "AI and data science should empower decision-making, not dictate it. Data is a powerful tool, but without human intuition, ethics, and critical thinking, it can lead to blind decisions and unintended consequences. I believe in using AI to enhance governance, optimize systems, and drive growth while ensuring fairness, transparency, and long-term impact."
        </blockquote>
      </section>

      {/* Call to Action */}
      <section className="cta text-center my-5">
        <h3>Let's build accountable systems</h3>
        <p>Bring me in to stabilize ops, ship AI responsibly, and get reporting audit-ready.</p>
        <a
          href="https://www.linkedin.com/in/mohamed-gad-msds-6b286b1b6/"
          className="btn btn-dark me-3"
        >
          <Linkedin size={18} className="me-1" /> Connect on LinkedIn
        </a>
        <a
          href="https://github.com/OldAlexhub"
          className="btn btn-outline-dark me-3"
          target="_blank"
          rel="noreferrer"
        >
          <Github size={18} className="me-1" /> GitHub
        </a>
        <Link to="/contactus" className="btn btn-success">
          <MessageSquare size={18} className="me-1" /> Contact Me
        </Link>
      </section>
    </div>
  );
};

export default Home;
